import i18n from "../../../i18n"; import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Megaphone, ChevronRight } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState, EmptyState } from "../../../components/ui";
import { fetchDashboardAnnouncements, sortAnnouncementsForDisplay } from "../../../services/announcementServices";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ROUTES, IMPORTANT_COLOR, GENERAL_COLOR, formatDate } from "../constants/announcementOptions";

const PAGE_SIZE = 10;

// Maps a raw API announcement to the card shape.
function normaliseItem(raw) {
  const created = raw.createdOn ?? raw.announcementCreatedOn ?? null;
  const cat = (raw.announcementType ?? raw.category ?? raw.announcementCategory ?? raw.priority ?? raw.announcementPriority ?? "").toLowerCase();
  const isImportant = cat === "important";
  const isArchived = (raw.announcementStatus ?? "").toLowerCase() === "archived";
  return {
    id: String(raw.announcementId),
    announcementId: raw.announcementId,
    tab: isArchived ? "archived" : isImportant ? "important" : "general",
    title: raw.announcementTitle ?? "",
    status: isImportant ? "Important" : "General",
    date: formatDate(created),
    dotColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
    content: raw.announcementBody ?? "",
    hasSupportingDocument: raw.hasSupportingDocument ?? false
  };
}

function buildData(items) {
  const result = { important: [], general: [] };
  for (const item of items) {
    if (item.tab === "archived") continue;
    if (result[item.tab]) result[item.tab].push(item); else
      result.general.push(item);
  }
  return result;
}

function TabPill({ tab, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold transition cursor-pointer " + (
          active ?
            "bg-primary text-white" :
            "per-file-hint hover:bg-white/70 hover:text-foreground")
      }>

      {tab.label}
      {tab.showBadge && tab.count > 0 &&
        <span className="grid place-items-center h-5 min-w-5 px-1 rounded-full text-[11px] font-bold text-white"
          style={{ backgroundColor: tab.badgeColor }}>
          {tab.count}
        </span>
      }
    </button>);

}

function AnnouncementCard({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full text-left flex items-start justify-between gap-3 bg-white rounded-xl px-5 py-4 border-l-4 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] hover:-translate-y-px transition cursor-pointer"
      style={{ borderLeftColor: item.dotColor }}>

      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: item.dotColor }} />
          <span className="text-[15px] font-semibold text-foreground truncate">{item.title}</span>
        </div>
        <span className="mt-1 ml-[18px] text-[13px] per-file-hint">
          {[item.status, item.date].filter(Boolean).join(" · ")}
        </span>
        {item.content &&
          <p className="mt-2 ml-[18px] text-[13px] leading-snug per-file-hint line-clamp-2 wrap-break-word">
            {item.content}
          </p>
        }
      </div>
      <ChevronRight className="h-5 w-5 per-file-hint shrink-0 self-center" />
    </button>);

}

export function ESSAnnouncementsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [archivedItems, setArchivedItems] = useState([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [archivedError, setArchivedError] = useState(null);

  useEffect(() => {
    fetchDashboardAnnouncements("All")
      .then((list) => setRawItems(sortAnnouncementsForDisplay(list).map(normaliseItem)))
      .catch((err) => setError(err.status === 401 ?
        "Session expired. Please refresh the page and log in again." :
        `Failed to load announcements: ${err.message}`))
      .finally(() => setLoading(false));
  }, []);

  const data = useMemo(() => buildData(rawItems), [rawItems]);

  const tabs = useMemo(() => [
    { key: "important", label: "Important", count: data.important.length, badgeColor: IMPORTANT_COLOR, showBadge: true },
    { key: "general", label: "General", count: data.general.length, badgeColor: GENERAL_COLOR, showBadge: true },
    { key: "archived", label: "Archived", count: archivedItems.length, badgeColor: "var(--status-submitted-fg)", showBadge: false }],
    [data, archivedItems]);

  const tabFromUrl = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl ?? "important");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (activeTab !== "archived" || archivedLoaded) return;
    fetchDashboardAnnouncements("Archived")
      .then((list) => setArchivedItems(sortAnnouncementsForDisplay(list).map(normaliseItem)))
      .catch((err) => setArchivedError(err.status === 401 ?
        "Session expired. Please refresh the page and log in again." :
        `Failed to load archived announcements: ${err.message}`))
      .finally(() => setArchivedLoaded(true));
  }, [activeTab, archivedLoaded]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const tabItems = activeTab === "archived" ? archivedItems : data[activeTab] ?? [];
  const isLoading = activeTab === "archived" ? !archivedLoaded : loading;
  const activeError = activeTab === "archived" ? archivedError : error;
  const totalPages = Math.max(Math.ceil(tabItems.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const visible = tabItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader back={<PageBackLink to="/" />} title={i18n.t("announcements.announcements2")} />

      <div className="flex gap-2.5 justify-center flex-wrap mb-6">
        {tabs.map((tab) =>
          <TabPill key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />
        )}
      </div>

      <div className="max-w-[960px] mx-auto flex flex-col gap-2.5">
        {isLoading ?
          <LoadingState /> :
          activeError ?
            <div className="p-8 text-center text-sm text-error">{activeError}</div> :
            tabItems.length > 0 ?
              <>
                {visible.map((item) =>
                  <AnnouncementCard key={item.id} item={item} onOpen={(it) => navigate(ROUTES.essDetail(it.id), { state: { item: it } })} />
                )}
                <div className="mt-4">
                  <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={tabItems.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage} />
                </div>
              </> :

              <EmptyState icon={Megaphone} title={i18n.t("announcements.noAnnouncementsAvailable")} />
        }
      </div>
    </AppLayout>);

}
